import { useEffect, useRef, useState } from "react";
import client, { resolveMedia } from "../lib/api";
import { Heart, Share2, MessageCircle, Volume2, VolumeX, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import ShareModal from "../components/ShareModal";

function ShortItem({ video, active, muted, onToggleMute, onShare }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) {
      v.currentTime = 0;
      v.play().catch(() => {});
      // Track view + watchtime heartbeat while active
      const sessionId = `${Math.random().toString(36).slice(2)}_${Date.now()}`;
      let lastBeat = 0;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const lang = navigator.language;
      const send = (completed = false) => {
        const cur = v.currentTime;
        const elapsed = Math.max(0, Math.min(cur - lastBeat, 60));
        if (elapsed < 0.5 && !completed) return;
        client.post(`/videos/${video.id}/track`, {
          session_id: sessionId,
          elapsed_seconds: elapsed,
          position: cur,
          duration: v.duration || 0,
          completed,
          timezone: tz,
          language: lang,
          screen_w: window.screen?.width,
          screen_h: window.screen?.height,
        }).catch(() => {});
        lastBeat = cur;
      };
      client.post(`/videos/${video.id}/view`).catch(() => {});
      const t = setInterval(() => { if (!v.paused) send(); }, 5000);
      return () => { clearInterval(t); send(); };
    } else {
      v.pause();
    }
  }, [active, video.id]);

  return (
    <div data-testid={`short-item-${video.id}`} className="snap-start h-[100svh] w-full relative flex items-center justify-center bg-black">
      <video
        ref={ref}
        src={resolveMedia(video.playback_url)}
        poster={resolveMedia(video.thumbnail)}
        loop
        muted={muted}
        playsInline
        preload="metadata"
        className="h-full w-auto max-w-full object-contain"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40 pointer-events-none" />

      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5 text-white">
        <button data-testid={`short-mute-${video.id}`} onClick={onToggleMute} className="w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:border-[#FF6B00]">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <div className="w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center"><Heart className="w-5 h-5" /></div>
        <div className="w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
        <button onClick={() => onShare(video)} data-testid={`short-share-${video.id}`} className="w-11 h-11 rounded-full bg-black/60 border border-white/20 hover:border-[#FF6B00] flex items-center justify-center"><Share2 className="w-5 h-5" /></button>
      </div>

      <div className="absolute left-4 right-20 bottom-10 text-white">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FFB800]">{video.category}</span>
        <h3 className="mt-1 text-lg font-bold leading-tight">{video.title}</h3>
        {video.description && <p className="mt-1 text-sm text-zinc-300 line-clamp-2">{video.description}</p>}
        <Link to={`/watch/${video.id}`} data-testid={`short-watch-${video.id}`} className="mt-3 inline-block text-[10px] uppercase tracking-widest text-[#FFB800] underline">Open in player</Link>
      </div>
    </div>
  );
}

export default function Shorts() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [shareVideo, setShareVideo] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    client.get("/videos?type=short").then((r) => setItems(r.data));
  }, []);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Number(e.target.getAttribute("data-idx"));
            setActive(idx);
          }
        });
      },
      { root: c, threshold: 0.6 }
    );
    c.querySelectorAll("[data-idx]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="h-[80vh] flex items-center justify-center text-center px-6" data-testid="shorts-empty">
        <div>
          <ChevronUp className="w-8 h-8 mx-auto text-[#FF6B00] aura-float" />
          <h2 className="mt-3 text-2xl font-bold text-white">No Shorts yet</h2>
          <p className="mt-2 text-zinc-500 text-sm">Vertical drops will appear here once uploaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="shorts-feed"
      className="h-[100svh] w-full overflow-y-auto snap-y snap-mandatory scrollbar-hide bg-black"
    >
      {items.map((v, i) => (
        <div key={v.id} data-idx={i}>
          <ShortItem video={v} active={i === active} muted={muted} onToggleMute={() => setMuted((m) => !m)} onShare={setShareVideo} />
        </div>
      ))}
      {shareVideo && <ShareModal video={shareVideo} onClose={() => setShareVideo(null)} />}
    </div>
  );
}
